
import { useCreateConnectionOauthState } from "../useCreateConnectionOauthState";
import { useState } from "react";
import axios from "axios";

// Define the response type
type AuthorizeResponse =
  | {
      flow: "authorization_code";
      url: string;
      state: string;
    }
  | {
      flow: "client_credentials";
      id: string;
    }
    ;

export const useClickCreateOauthConnectionUx = (
  setErrorMessage: (error: string) => void,
  setSuccess: (success: boolean) => void,
  linkHeaders?: Record<string, unknown>
) => {
  const { createConnectionOauthState } = useCreateConnectionOauthState();

  const [state, setState] = useState<string | undefined>(undefined);

  const trigger = async ({
    secretId,
    connectionOauthDefinitionId,
    formData,
    setLoading,
    requestId,
  }: {
    setLoading: (loading: boolean) => void;
    secretId: string;
    formData: { [K: string]: unknown };
    connectionOauthDefinitionId: number;
    requestId: number;
  }) => {
    try {
      setLoading(true);

      const connectionOauthState = (await createConnectionOauthState({
        secretId,
        formData,
        connectionOauthDefinitionId,
        requestId,
        linkHeaders,
      })) as AuthorizeResponse;

      // Handle client_credentials type
      if (connectionOauthState.flow === "client_credentials") {
        setLoading(false);
        setSuccess(true);

        // Send success message to parent
        window.parent.postMessage(
          {
            messageType: "LINK_SUCCESS",
            message: connectionOauthState
          },
          "*"
        );
        return;
      }

      // Handle authorization_code type (existing flow)
      const { url, state } = connectionOauthState;

      setState(state);

      if (!url) {
        throw new Error("No url");
      }

      if (!state) {
        throw new Error("No state");
      }

      window.open(url, "connect", "width=500,height=800");
    } catch (error) {
      setLoading(false);

      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message;
        const errorStatus = error.response?.status;

        if (errorStatus === 404 && errorMessage === "Resource not found") {
          setErrorMessage("The session has expired. Please try again.");
        } else if ((errorStatus === 402 || errorStatus === 429) && errorMessage === "Please try again later or upgrade your plan") {
          setErrorMessage("Please try again later or upgrade your plan.");
        } else if (errorMessage) {
          // Show the error message from the API
          setErrorMessage(errorMessage);
        } else {
          // Fallback error message
          setErrorMessage("Something went wrong. Please try again.");
        }
      } else {
        // Non-axios errors
        setErrorMessage("Something went wrong. Please try again.");
      }

      console.error(error);
    }
  };

  return {
    trigger,
    state,
  };
};
